// @ts-nocheck
import React, { memo, useMemo } from "react";
import { PieChart } from "react-minimal-pie-chart";
import { useAppContext } from "@context/AppContext";

/**
 * PieDist Component - Displays distribution data in a pie chart format
 * Shows percentage distribution with color-coded labels and NFT preview
 */
const PieDist = memo(({ pieData = [], preset = "", sliders = [], previewLink = "" }) => {
  const { themeConfig } = useAppContext();

  // Memoized pie chart data to prevent recalculation on every render
  const pieChartData = useMemo(() => {
    if (!pieData || pieData.length < 3 || !themeConfig?.DISTRO_COLOR) {
      return [];
    }

    return [
      { 
        value: Math.round(pieData[0] || 0), 
        color: themeConfig.DISTRO_COLOR.beneficiary 
      },
      { 
        value: Math.round(pieData[1] || 0), 
        color: themeConfig.DISTRO_COLOR.artist 
      },
      { 
        value: Math.round(pieData[2] || 0), 
        color: themeConfig.DISTRO_COLOR.sponsor 
      },
      /*{ 
        value: Math.round(pieData[3] || 0), 
        color: themeConfig.DISTRO_COLOR.seller 
      },*/
    ];
  }, [pieData, themeConfig?.DISTRO_COLOR]);

  // Memoized label formatter for pie chart
  const labelFormatter = useMemo(() => {
    return ({ dataEntry }) => {
      const value = dataEntry.value;
      if (value < 2) return "";
      if (value >= 2 && value <= 3) return value.toString();
      return `${value}%`;
    };
  }, []);

  // Memoized label style to prevent object recreation
  const labelStyle = useMemo(() => ({ fontSize: "4px" }), []);

  // Memoized slider labels to prevent recalculation
  const sliderLabels = useMemo(() => {
    if (!sliders || !Array.isArray(sliders) || !themeConfig?.DISTRO_COLOR) {
      return [];
    }

    return sliders.map((slider, index) => {
      const categoryKey = slider.category?.replace("fundraiser", "sponsor") || "";
      const backgroundColor = themeConfig.DISTRO_COLOR[categoryKey] || "#000";
      
      return {
        id: `${slider.name}-${index}`,
        name: slider.name,
        backgroundColor,
      };
    });
  }, [sliders, themeConfig?.DISTRO_COLOR]);

  // Early return if no preset
  if (!preset || preset.length === 0) {
    return null;
  }

  return (
    <div className="mint__dist">
      <h3 
        className="mint-preview__title" 
        style={{ color: "var(--web-primary-color)" }}
        aria-label={`Distribution preset: ${preset}`}
      >
        {preset}
      </h3>
      
      <div className="pie-wrapper">
        <PieChart
          className="pie"
          data={pieChartData}
          label={labelFormatter}
          labelStyle={labelStyle}
          startAngle={-90}
          labelPosition={87}
          radius={40}
          animate
          aria-label="Distribution pie chart"
        />
        {previewLink && (
          <img 
            src={previewLink} 
            className="pie-nft" 
            alt="NFT preview for distribution chart"
            loading="lazy"
          />
        )}
      </div>
      
      {sliderLabels.length > 0 && (
        <div className="pie-labels__wrapper" role="list" aria-label="Distribution legend">
          {sliderLabels.map((label) => (
            <div 
              key={label.id} 
              className="pie-label__wrapper"
              role="listitem"
            >
              <div
                className="pie-label__color-box"
                style={{ backgroundColor: label.backgroundColor }}
                aria-hidden="true"
              />
              <span className="pie-label">{label.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

PieDist.displayName = "PieDist";

export default PieDist;
